/**
 * Admin Validation Error Handler
 *
 * Utilities for handling LAST_ADMIN_CANNOT_LEAVE errors from the backend
 * and providing clear, actionable feedback to users.
 */

export interface AdminValidationError {
  code: string;
  message: string;
  details?: Array<{
    field: string;
    message: string;
    code?: string;
  }>;
}

/**
 * Check if an error is a "last admin cannot leave" validation error
 */
export function isLastAdminError(error: any): boolean {
  if (!error) return false;

  // Check for LAST_ADMIN_CANNOT_LEAVE in various error formats
  const errorMessage = error?.message || error?.error?.message || '';
  const errorCode = error?.code || error?.error?.code || '';
  const errorDetails = error?.details || error?.error?.details || {};
  const validationErrors = errorDetails?.validation_errors || [];

  // Check error code
  if (errorCode === 'LAST_ADMIN_CANNOT_LEAVE') {
    return true;
  }

  // Check message contains indicator
  if (errorMessage.toLowerCase().includes('last administrator')) {
    return true;
  }

  // Check validation details (new structure)
  if (Array.isArray(validationErrors)) {
    return validationErrors.some((detail: any) =>
      detail?.message?.includes('LAST_ADMIN_CANNOT_LEAVE') ||
      detail?.code === 'LAST_ADMIN_CANNOT_LEAVE'
    );
  }

  // Check old validation details structure
  if (Array.isArray(errorDetails)) {
    return errorDetails.some((detail: any) =>
      detail?.message?.includes('LAST_ADMIN_CANNOT_LEAVE') ||
      detail?.code === 'LAST_ADMIN_CANNOT_LEAVE'
    );
  }

  return false;
}

/**
 * Extract the entity name (organization or team) from the error message
 */
export function extractEntityName(error: any): string | null {
  const errorMessage = error?.message || error?.error?.message || '';
  const validationErrors = error?.error?.details?.validation_errors || error?.details?.validation_errors || [];

  // Try validation error messages first (more specific)
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    const validationMessage = validationErrors[0]?.message || '';
    // Format: "LAST_ADMIN_CANNOT_LEAVE: You are the last admin of Acme (organization)"
    const match = validationMessage.match(/last admin of ([^(]+)\s*\(/i);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  // Fall back to main error message
  // Message format: "Cannot leave organization: You are the last administrator of "Entity Name"..."
  const match = errorMessage.match(/last administrator of [""]([^""]+)[""]/)
    || errorMessage.match(/last administrator of ([^.]+)\./);

  if (match && match[1]) {
    return match[1].trim();
  }

  return null;
}

/**
 * Get the entity type (organization or team) from the error
 */
export function extractEntityType(error: any): 'organization' | 'team' | 'entity' {
  const errorMessage = (error?.message || error?.error?.message || '').toLowerCase();
  const validationErrors = error?.error?.details?.validation_errors || error?.details?.validation_errors || [];

  // Check validation errors first (more specific)
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    const validationMessage = (validationErrors[0]?.message || '').toLowerCase();
    if (validationMessage.includes('(organization)')) {
      return 'organization';
    } else if (validationMessage.includes('(team)')) {
      return 'team';
    }
  }

  // Fall back to main error message
  if (errorMessage.includes('organization')) {
    return 'organization';
  } else if (errorMessage.includes('team')) {
    return 'team';
  }

  return 'entity';
}

/**
 * Get a user-friendly error message for the last admin error
 */
export function getLastAdminErrorMessage(error: any, fallbackEntityType?: 'organization' | 'team'): string {
  const entityName = extractEntityName(error);
  const entityType = extractEntityType(error) || fallbackEntityType || 'entity';

  if (entityName) {
    return `You cannot leave "${entityName}" because you are the last administrator. Please transfer the admin role to another member before leaving, or delete the ${entityType} entirely.`;
  }

  return `You cannot leave this ${entityType} because you are the last administrator. Please transfer the admin role to another member before leaving, or delete the ${entityType} entirely.`;
}

/**
 * Get the title for an error dialog
 */
export function getLastAdminErrorTitle(entityType?: 'organization' | 'team'): string {
  const type = entityType || 'Entity';
  return `Cannot Leave ${type.charAt(0).toUpperCase() + type.slice(1)}`;
}

/**
 * Get action suggestions for resolving the last admin error
 */
export function getLastAdminErrorActions(entityType: 'organization' | 'team'): string[] {
  const actions = [
    `Add another admin to the ${entityType}`,
    `Transfer your admin role to an existing member`,
    `Delete the ${entityType} if it's no longer needed`
  ];

  return actions;
}

/**
 * Format the complete error message with actions
 */
export function formatLastAdminError(error: any, fallbackEntityType?: 'organization' | 'team'): {
  title: string;
  message: string;
  actions: string[];
  entityType: 'organization' | 'team' | 'entity';
} {
  const entityType = extractEntityType(error) || fallbackEntityType || 'entity';
  const message = getLastAdminErrorMessage(error, fallbackEntityType);
  const title = getLastAdminErrorTitle(entityType === 'entity' ? undefined : entityType);
  const actions = entityType === 'entity' ? [] : getLastAdminErrorActions(entityType);

  return {
    title,
    message,
    actions,
    entityType
  };
}
